import { Conversation } from '@/types/conversation'
import ProgressIndicator from './ProgressIndicator'

interface StatusPanelProps {
  conversation: Conversation
  isConnected: boolean
}

export default function StatusPanel({ conversation, isConnected }: StatusPanelProps) {
  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'initial':
        return 'bg-blue-100 text-blue-800'
      case 'building':
        return 'bg-yellow-100 text-yellow-800'
      case 'refining':
        return 'bg-purple-100 text-purple-800'
      case 'complete':
        return 'bg-green-100 text-green-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="w-80 h-full bg-gray-50 border-l overflow-hidden flex flex-col">
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Conversation Status</h3>
        <div className="bg-white rounded-lg p-3 space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">ID:</span>
            <span className="text-xs font-mono text-gray-800">
              {conversation.id?.slice(0, 8)}...
            </span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Stage:</span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStageColor(conversation.stage)}`}>
              {conversation.stage}
            </span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Mode:</span>
            <span className="text-sm">
              {conversation.conversationalMode ? 'Conversational' : 'Single-shot'}
            </span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Messages:</span>
            <span className="text-sm">{conversation.messages?.length || 0}</span>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Progress</h3>
        <ProgressIndicator progress={conversation.progress || 0} />
      </div>

      {conversation.blocks && conversation.blocks.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">
            Workflow Structure ({conversation.blocks.length} blocks)
          </h3>
          <div className="bg-white rounded-lg p-3 max-h-48 overflow-y-auto">
            <div className="space-y-1">
              {conversation.blocks.map((block, index) => (
                <div key={index} className="flex items-center text-sm">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                  <span className="text-gray-700">{block.name || block.id}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {conversation.flows && conversation.flows.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">
            Connections ({conversation.flows.length})
          </h3>
          <div className="bg-white rounded-lg p-3 max-h-32 overflow-y-auto">
            <div className="space-y-1 text-xs font-mono">
              {conversation.flows.map((flow, index) => (
                <div key={index} className="text-gray-600">
                  {flow.from} → {flow.to}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="pt-2">
        <div className="flex items-center space-x-2 text-xs text-gray-500">
          <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
          <span>WebSocket {isConnected ? 'connected' : 'disconnected'}</span>
        </div>
      </div>
      </div>
    </div>
  )
}